import { getIdToken } from '../../utils/keycloak';
import { getBackendUrl } from '../../utils/utils';
import { getEnv } from '../../utils/runtimeConfig';
import i18n from 'i18next';


const BASE_URL = getBackendUrl();

// Debug logging
console.log('API Service - BASE_URL:', BASE_URL);
console.log('API Service - REACT_APP_BACKEND_HOST:', getEnv('REACT_APP_BACKEND_HOST'));

// Fallback messages for a failed request, used when the server sent no `detail`.
// Resolved when the error is produced so they follow the current UI language.
const httpErrorDetail = (status: number) => i18n.t('common.httpError', { status });
const apiErrorMessage = (status: number) => i18n.t('common.apiError', { status });

/**
 * API service for making authenticated requests
 */
export const api = {
  /**
   * Make a GET request
   * @param endpoint - API endpoint
   * @returns Promise resolving to JSON response
   */
  async get<T>(endpoint: string): Promise<T> {
    try {
      const token = await getIdToken();
      const fullUrl = `${BASE_URL}${endpoint}`;
      console.log('API GET request to:', fullUrl);
      
      const response = await fetch(fullUrl, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (!response.ok) {
        console.error('API GET failed:', response.status, response.statusText, 'URL:', fullUrl);
        // Mirrors the write helpers below: attach both `.status` (a caller that wants to
        // tell "this endpoint does not exist on this backend" (404, e.g. a frontend
        // deployed ahead of its API) from a real failure cannot do it from the message)
        // and `.response` (so callers reading `error.response.detail` for a server-sent
        // error message, as the write helpers' callers do, get one from GET too).
        const errorData = await response.json().catch(() => ({ detail: httpErrorDetail(response.status) }));
        const error = new Error(errorData.detail || apiErrorMessage(response.status));
        (error as any).status = response.status;
        (error as any).response = errorData;
        throw error;
      }
      
      return await response.json() as T;
    } catch (error) {
      console.error(`GET request to ${endpoint} failed:`, error);
      throw error;
    }
  },

  /**
   * Make a POST request
   * @param endpoint - API endpoint
   * @param data - Request payload
   * @returns Promise resolving to JSON response
   */
  async post<T, U = any>(endpoint: string, data: U): Promise<T> {
    try {
      const token = await getIdToken();
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: httpErrorDetail(response.status) }));
        const error = new Error(errorData.detail || apiErrorMessage(response.status));
        (error as any).status = response.status;
        (error as any).response = errorData;
        throw error;
      }
      
      return await response.json() as T;
    } catch (error) {
      console.error(`POST request to ${endpoint} failed:`, error);
      throw error;
    }
  },

  /**
   * Make a PUT request
   * @param endpoint - API endpoint
   * @param data - Request payload
   * @returns Promise resolving to JSON response
   */
  async put<T, U = any>(endpoint: string, data: U): Promise<T> {
    try {
      const token = await getIdToken();
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: httpErrorDetail(response.status) }));
        const error = new Error(errorData.detail || apiErrorMessage(response.status));
        (error as any).status = response.status;
        (error as any).response = errorData;
        throw error;
      }
      
      return await response.json() as T;
    } catch (error) {
      console.error(`PUT request to ${endpoint} failed:`, error);
      throw error;
    }
  },

  /**
   * Make a PATCH request
   *
   * Same shape as `put`, for endpoints whose body is a partial update — the join
   * settings panel sends only the fields the admin actually changed, so a PUT would
   * mean sending (and risking clobbering) the ones they didn't touch.
   *
   * @param endpoint - API endpoint
   * @param data - Partial payload; omitted fields are left unchanged server-side
   * @returns Promise resolving to JSON response
   */
  async patch<T, U = any>(endpoint: string, data: U): Promise<T> {
    try {
      const token = await getIdToken();
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "PATCH",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: httpErrorDetail(response.status) }));
        const error = new Error(errorData.detail || apiErrorMessage(response.status));
        (error as any).status = response.status;
        (error as any).response = errorData;
        throw error;
      }

      return await response.json() as T;
    } catch (error) {
      console.error(`PATCH request to ${endpoint} failed:`, error);
      throw error;
    }
  },

  /**
   * Make a DELETE request
   * @param endpoint - API endpoint
   * @returns Promise resolving to JSON response
   */
  async delete<T>(endpoint: string): Promise<T> {
    try {
      const token = await getIdToken();
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: httpErrorDetail(response.status) }));
        const error = new Error(errorData.detail || apiErrorMessage(response.status));
        (error as any).status = response.status;
        (error as any).response = errorData;
        throw error;
      }
      // Only try to parse JSON if there is content
      const text = await response.text();
      if (!text) return {} as T;
      return JSON.parse(text) as T;
    } catch (error) {
      console.error(`DELETE request to ${endpoint} failed:`, error);
      throw error;
    }
  }
};